import { AdminLayout } from "@/components/admin/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Users, TrendingUp, MousePointerClick, Target } from "lucide-react";

interface UTMStats {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  count: number;
  with_demo: number;
  with_purchase: number;
}

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#14b8a6', '#f97316'];

const UTM = () => {
  // Fetch UTM statistics
  const { data: utmStats, isLoading } = useQuery({
    queryKey: ['utm-stats'],
    queryFn: async () => {
      // Get all users with UTM data
      const { data: users, error } = await supabase
        .from('telegram_users')
        .select('utm_source, utm_medium, utm_campaign, last_demo_at, last_purchase_at')
        .not('utm_source', 'is', null);

      if (error) throw error;

      // Aggregate by source
      const sourceMap = new Map<string, { count: number; with_demo: number; with_purchase: number }>();
      const mediumMap = new Map<string, { count: number; with_demo: number; with_purchase: number }>();
      const campaignMap = new Map<string, { count: number; with_demo: number; with_purchase: number }>();

      users?.forEach(user => {
        const hasDemoFn = user.last_demo_at ? 1 : 0;
        const hasPurchaseFn = user.last_purchase_at ? 1 : 0;

        // By source
        if (user.utm_source) {
          const existing = sourceMap.get(user.utm_source) || { count: 0, with_demo: 0, with_purchase: 0 };
          sourceMap.set(user.utm_source, {
            count: existing.count + 1,
            with_demo: existing.with_demo + hasDemoFn,
            with_purchase: existing.with_purchase + hasPurchaseFn,
          });
        }

        // By medium
        if (user.utm_medium) {
          const existing = mediumMap.get(user.utm_medium) || { count: 0, with_demo: 0, with_purchase: 0 };
          mediumMap.set(user.utm_medium, {
            count: existing.count + 1,
            with_demo: existing.with_demo + hasDemoFn,
            with_purchase: existing.with_purchase + hasPurchaseFn,
          });
        }

        // By campaign
        if (user.utm_campaign) {
          const existing = campaignMap.get(user.utm_campaign) || { count: 0, with_demo: 0, with_purchase: 0 };
          campaignMap.set(user.utm_campaign, {
            count: existing.count + 1,
            with_demo: existing.with_demo + hasDemoFn,
            with_purchase: existing.with_purchase + hasPurchaseFn,
          });
        }
      });

      const bySource = Array.from(sourceMap.entries())
        .map(([source, stats]) => ({ name: source, ...stats }))
        .sort((a, b) => b.count - a.count);

      const byMedium = Array.from(mediumMap.entries())
        .map(([medium, stats]) => ({ name: medium, ...stats }))
        .sort((a, b) => b.count - a.count);

      const byCampaign = Array.from(campaignMap.entries())
        .map(([campaign, stats]) => ({ name: campaign, ...stats }))
        .sort((a, b) => b.count - a.count);

      const totalWithUtm = users?.length || 0;
      const totalWithDemo = users?.filter(u => u.last_demo_at).length || 0;
      const totalWithPurchase = users?.filter(u => u.last_purchase_at).length || 0;

      return {
        bySource,
        byMedium,
        byCampaign,
        totalWithUtm,
        totalWithDemo,
        totalWithPurchase,
      };
    },
  });

  // Fetch total users for comparison
  const { data: totalUsers } = useQuery({
    queryKey: ['total-users-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('telegram_users')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      return count || 0;
    },
  });

  const utmCoverage = totalUsers ? Math.round((utmStats?.totalWithUtm || 0) / totalUsers * 100) : 0;
  const demoConversion = utmStats?.totalWithUtm 
    ? Math.round((utmStats.totalWithDemo / utmStats.totalWithUtm) * 100) 
    : 0;
  const purchaseConversion = utmStats?.totalWithUtm 
    ? Math.round((utmStats.totalWithPurchase / utmStats.totalWithUtm) * 100) 
    : 0;

  return (
    <AdminLayout title="UTM Аналитика" description="Анализ источников трафика и эффективности рекламных кампаний">
      <div className="space-y-6">

        {/* Summary cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">С UTM-метками</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{utmStats?.totalWithUtm || 0}</div>
              <p className="text-xs text-muted-foreground">
                {utmCoverage}% от всех пользователей
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Источников</CardTitle>
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{utmStats?.bySource.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                уникальных utm_source
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Демо конверсия</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{demoConversion}%</div>
              <p className="text-xs text-muted-foreground">
                {utmStats?.totalWithDemo || 0} создали демо
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Покупки</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{purchaseConversion}%</div>
              <p className="text-xs text-muted-foreground">
                {utmStats?.totalWithPurchase || 0} купили
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* By Source Chart */}
          <Card>
            <CardHeader>
              <CardTitle>По источникам (utm_source)</CardTitle>
            </CardHeader>
            <CardContent>
              {utmStats?.bySource && utmStats.bySource.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={utmStats.bySource.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="count" fill="#8b5cf6" name="Пользователей" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="with_demo" fill="#10b981" name="С демо" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="with_purchase" fill="#f59e0b" name="Купили" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Нет данных по UTM-меткам
                </div>
              )}
            </CardContent>
          </Card>

          {/* Source Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Распределение по источникам</CardTitle>
            </CardHeader>
            <CardContent>
              {utmStats?.bySource && utmStats.bySource.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={utmStats.bySource.slice(0, 8)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {utmStats.bySource.slice(0, 8).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Нет данных
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tables */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* By Medium */}
          <Card>
            <CardHeader>
              <CardTitle>По каналам (utm_medium)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Канал</TableHead>
                    <TableHead className="text-right">Юзеров</TableHead>
                    <TableHead className="text-right">Демо</TableHead>
                    <TableHead className="text-right">Покупок</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {utmStats?.byMedium.map((item) => (
                    <TableRow key={item.name}>
                      <TableCell>
                        <Badge variant="outline">{item.name}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{item.count}</TableCell>
                      <TableCell className="text-right text-green-500">{item.with_demo}</TableCell>
                      <TableCell className="text-right text-amber-500">{item.with_purchase}</TableCell>
                    </TableRow>
                  ))}
                  {(!utmStats?.byMedium || utmStats.byMedium.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Нет данных
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* By Campaign */}
          <Card>
            <CardHeader>
              <CardTitle>По кампаниям (utm_campaign)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Кампания</TableHead>
                    <TableHead className="text-right">Юзеров</TableHead>
                    <TableHead className="text-right">Демо</TableHead>
                    <TableHead className="text-right">Покупок</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {utmStats?.byCampaign.map((item) => (
                    <TableRow key={item.name}>
                      <TableCell>
                        <Badge variant="secondary">{item.name}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{item.count}</TableCell>
                      <TableCell className="text-right text-green-500">{item.with_demo}</TableCell>
                      <TableCell className="text-right text-amber-500">{item.with_purchase}</TableCell>
                    </TableRow>
                  ))}
                  {(!utmStats?.byCampaign || utmStats.byCampaign.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Нет данных
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UTM;